import { Id, RenderProps, isSuccess, useBulkEdit, usePaginatedList, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { mapToBulkRequestWithFilterId } from "../../../../../shared/api/mappings";
import { contactService } from "../../../shared/api/contactService";
import { createInvitationListService } from "../../../shared/api/invitationListService";
import { AttachContactToInvitationListDrawer } from "../../../shared/AttachContactsToInvitationListDrawer";
import { IInvitationListAttachBulkEditContactsFormState } from "../../../shared/types/invitationList";

interface Props extends RenderProps {
	contactFilterId: Id | null;
}

export const InvitationListAttachBulkEditContactsDrawer: FC<Props> = ({
	open,
	handleOpen,
	handleClose,
	contactFilterId,
}) => {
	const { t } = useTranslation();
	const { selectedRows } = useBulkEdit();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const { searchParams } = usePaginatedList();

	const onSubmit = useCallback(
		async ({ event, ...values }: IInvitationListAttachBulkEditContactsFormState) => {
			if (!event) {
				throw new Error("Event is missing");
			}
			const service = createInvitationListService(event.id);

			const response = await service.attachContactsByBulkEdit({
				...mapToBulkRequestWithFilterId(selectedRows, contactFilterId, searchParams?.search),
				...values,
			});

			if (isSuccess(response)) {
				showToast({ type: "success", title: t("Contacts"), body: t("Contacts successfully added") });
				await queryClient.invalidateQueries({ queryKey: service.indexKey });
				await queryClient.invalidateQueries({ queryKey: contactService.indexKey });
				return true;
			}

			return false;
		},
		[queryClient, selectedRows, showToast, t, contactFilterId, searchParams?.search],
	);

	return (
		<AttachContactToInvitationListDrawer
			open={open}
			handleOpen={handleOpen}
			handleClose={handleClose}
			onSubmit={onSubmit}
		/>
	);
};
